/**
 * Multi-Tenancy Type Definitions
 *
 * This file defines the tenant configuration structure for supporting
 * multiple companies/organizations within the THEO platform.
 *
 * USAGE:
 * - Import types: import { TenantConfig } from '@/types/tenant.types'
 * - Access via context: const { tenant } = useTenant()
 *
 * FIRESTORE COLLECTION: tenants/{tenantId}
 *
 * @see CLAUDE.md - Architecture Standards section for usage rules
 */

/**
 * Theme configuration for tenant branding colors
 *
 * All colors should be valid CSS color values (hex, rgb, hsl)
 * These are injected as CSS variables by TenantProvider
 */
export interface TenantTheme {
  /** Primary brand color (e.g., "#1a2238" - Deep Navy) */
  primaryColor: string;

  /** Secondary/accent brand color (e.g., "#c5a065" - Gold) */
  secondaryColor: string;

  /** Light accent color for borders/highlights (e.g., "#e2e8f0") */
  accentColor?: string;

  /** Dark background color (e.g., "#0f1521") */
  darkColor?: string;

  /** Success state color (default: "#10b981") */
  successColor?: string;

  /** Error/danger state color (default: "#ef4444") */
  dangerColor?: string;

  /** Warning state color (default: "#f59e0b") */
  warningColor?: string;
}

/**
 * Visual branding assets for the tenant
 */
export interface TenantBranding {
  /** URL to the company logo image */
  logoUrl?: string;

  /** URL to the favicon */
  faviconUrl?: string;

  /** Inline SVG markup for logo (alternative to logoUrl) */
  logoSvg?: string;

  /** URL to Apple touch icon for PWA */
  appleTouchIcon?: string;

  /** URL to PWA icon (192x192) */
  pwaIcon192?: string;

  /** URL to PWA icon (512x512) */
  pwaIcon512?: string;
}

/**
 * Business/company details for the tenant
 */
export interface TenantBusiness {
  /** Australian Business Number */
  abn?: string;

  /** Business street address */
  address?: string;

  /** Business phone number */
  phone?: string;

  /** Business email address */
  email?: string;

  /** Business website URL */
  website?: string;

  /**
   * Public Google review link for this tenant (e.g. a g.page/r/… short link).
   * Used by the review-request SMS sent when an install deal is marked as
   * billed (functions/src/sms/review-sms.ts). Server reads it via
   * loadSmsTenantContext; falls back to the GOOGLE_REVIEW_URL functions env
   * var, then skips the SMS when neither is set.
   */
  googleReviewUrl?: string;

  /** Default tax rate (e.g., 0.10 for 10% GST) */
  defaultTaxRate?: number;

  /** Default currency code (e.g., "AUD") */
  currency?: string;

  /** Timezone for business operations (e.g., "Australia/Sydney") */
  timezone?: string;

  /**
   * BCP-47 locale for date / time / number formatting (e.g., "en-AU").
   * Missing falls back to DEFAULT_TENANT_LOCALE ('en-AU') via
   * getTenantLocale(tenant) in src/utils/locale.ts.
   *
   * Added May 2026 per SaaS-readiness audit. The app currently hardcodes
   * 'en-AU' in ~397 toLocale*() calls; those migrate to getTenantLocale()
   * incrementally so a non-AU tenant formats dates/currency in their locale.
   */
  locale?: string;

  /**
   * Per-tenant office location override. Drives the map view's office
   * pin + tooltip, the default map center on app load, and the fallback
   * coordinates applied to jobs without geocoded lat/lng. Missing fields
   * fall back to DEFAULT_OFFICE_LOCATION (src/utils/officeLocation.ts).
   *
   * Customer-facing surface: the office marker tooltip is visible on the
   * Live Map View, which is often shared on office TVs — so this MUST be
   * tenant-correct, not Techery's Bowral details.
   *
   * Added May 2026 per SaaS-readiness audit. Reader:
   * `getOfficeLocation(tenant)` / `getDefaultMapCenter(tenant)` /
   * `getJobFallbackCoords(tenant)`.
   */
  officeLocation?: {
    lat?: number;
    lng?: number;
    name?: string;
    address?: string;
  };

  /**
   * Per-tenant labour rate overrides (AUD/hour, ex GST). Each field is
   * optional; missing fields fall back to the platform defaults in
   * `src/utils/labourRates.ts` (DEFAULT_QUOTE_LABOUR_RATES /
   * DEFAULT_FAULT_LABOUR_RATE_PER_HOUR).
   *
   * - `faultPerHour` drives FaultCosts.labourCost accumulation (internal cost).
   * - `sellPerHour` drives client-facing labour pricing (master plan §4.7
   *   LABOUR_SELL_RATE — used by CableSchedule sell-side math etc.).
   * - `quote.*` drives proposal pricing for the three team configurations.
   *
   * Added May 2026 per SaaS-readiness audit — each tenant charges
   * different rates, so these can no longer be hardcoded platform-wide.
   * Reader: `getFaultLabourRatePerHour(tenant)` /
   * `getLabourSellRatePerHour(tenant)` / `getQuoteLabourRates(tenant)`
   * in src/utils/labourRates.ts.
   */
  labourRates?: {
    faultPerHour?: number;
    sellPerHour?: number;
    quote?: {
      '1-tech'?: number;
      '2-techs'?: number;
      '2-senior-techs'?: number;
    };
  };

  /**
   * Deposit-invoice configuration (P2.0.8).
   *
   * When this block is set AND `featureFlags.enableDepositInvoices` is
   * true, deals at WON stage with `value > thresholdIncGst` surface a
   * "Raise Deposit Invoice" hero button. The deposit invoice is raised
   * for `depositPercent * totalIncGst` and the balance is captured via
   * a second Stripe PaymentIntent at billing time.
   *
   * Absent block → server-side `getDepositConfig()` falls back to the
   * defaults (threshold $2000, 50%). Surfacing both fields lets a
   * tenant lower the threshold (e.g. $1000) or raise it ($5000) without
   * a code change.
   */
  depositInvoice?: {
    /** Inc-GST threshold in DOLLARS. Deals above this trigger the prompt. */
    thresholdIncGst: number;
    /** Deposit percentage as a decimal (0.5 = 50%). */
    depositPercent: number;
  };
}

/**
 * Feature flags to enable/disable functionality per tenant
 *
 * All flags default to false if not specified
 */
export interface TenantFeatureFlags {
  /** Enable fault pipeline for issue tracking */
  enableFaultPipeline: boolean;

  /** Enable project management features */
  enableProjectManagement: boolean;

  /** Enable equipment provisioning module */
  enableProvisioning: boolean;

  /** Enable design playground tool */
  enablePlayground: boolean;

  /** Enable tech consultation requests */
  enableTechConsults: boolean;

  /** Show Starlink kit options in forms */
  enableStarlinkSupport: boolean;

  /** Enable quote generation features */
  enableQuoteGeneration: boolean;

  /** Enable AI-powered greeting messages */
  enableAIGreetings: boolean;

  /** Enable weekly tech quiz feature */
  enableWeeklyQuiz: boolean;

  /** Enable employee of the month voting system */
  enableEmployeeVoting: boolean;

  /** Enable internal messaging system */
  enableMessaging: boolean;

  /** Enable customer portal access */
  enableCustomerPortal: boolean;

  /** Enable inventory management */
  enableInventory: boolean;

  /** Enable timesheet tracking */
  enableTimesheets: boolean;

  /** Enable map/GPS features */
  enableMaps: boolean;

  /** Enable UniFi Design Center PDF import and AI BOM extraction in Playground */
  enableUnifiDesignImport: boolean;

  /** Enable Deal Workspace system (replaces legacy sales pipeline) */
  enableDealWorkspace: boolean;

  /**
   * Projects v2 workspace flag.
   *
   * NOTE (June 2026 cutover): Projects v2 is now the LIVE projects experience
   * at `/home/projects` for ALL signed-in users — this flag no longer gates
   * ROUTING (the old `/home/dev/projects-v2/*` dev routes redirect to the live
   * paths, and the FeatureGate route wrapper was removed). The flag is retained
   * for any future per-tenant projects toggles + back-compat with the
   * `enable:projects-v2` script; it does NOT control access to the projects UI
   * today. (The separate `enableProjectsV2Cutover` flag below still gates the
   * job-creating auto-actions.)
   */
  enableProjectsV2?: boolean;

  /**
   * Projects v2 PRODUCTION cutover gate (Phase 6/10). Distinct from
   * `enableProjectsV2` (which gates the dev workspace at /home/dev/
   * projects-v2). While FALSE, the project stage-transition auto-actions
   * that create REAL jobs (per-dwelling pre-wire / trim-out) no-op — they
   * write a completion marker but create nothing — so dev/test projects
   * never leak jobs into the live scheduler. Flip TRUE only at cutover.
   * Read server-side by the auto-action handlers.
   */
  enableProjectsV2Cutover?: boolean;

  /**
   * Enable deposit invoices (P2.0.8). When true AND the tenant has
   * Stripe Connect set up, deals at WON stage with
   * `value > business.depositInvoice.thresholdIncGst` (default $2000)
   * surface a "Raise Deposit Invoice" hero button. Default false; flip
   * on per-tenant after end-to-end testing.
   */
  enableDepositInvoices?: boolean;

  /**
   * Phase 4 — flip on to use the native proposal system (Phases 1-3
   * implementation). When false, the workspace shows only the BP
   * cards. When true, the NativeProposalStatusCard becomes the
   * primary proposal UI and Better Proposals drops to a fallback
   * read-only display for legacy in-flight deals.
   *
   * Migration plan (docs/NATIVE_PROPOSALS_ARCHITECTURE.md §7):
   *   Phase 4 ships with default false on every tenant
   *   Tenant admin flips on after templates + sender domain are set up
   *   30-day clean parallel-run → flip default to true
   *   90-day clean → drop the BP integration entirely
   */
  useNativeProposals?: boolean;

  /**
   * During the BP→native parallel-run period, both systems can fire on
   * the same deal. This flag controls whether the BP webhook handlers
   * remain active. Set false on a tenant to "soft-deprecate" BP for
   * that tenant only (BP webhooks ignored, native is the only path).
   *
   * Defaults to true (BP active) so Phase 4 doesn't break existing
   * deals. Phase 5 cutover sets this false; Phase 6 deletes the BP
   * code entirely.
   */
  enableLegacyBetterProposals?: boolean;

  /**
   * Proposal Builder V2 route + launcher; admins always see it. Default
   * false. A `featureFlags.*` sub-key — no TENANT_CONFIG_KEYS change.
   */
  proposalBuilderV2?: boolean;
}

/**
 * Email-based passwordless login configuration per tenant
 *
 * Each tenant has its own verified Resend sender domain. The Cloud Function
 * `requestEmailLogin` reads this config to send branded, tenant-correct
 * emails. Optional — tenants without this config cannot use email login,
 * and their users fall back to PIN.
 */
export interface TenantEmailLoginConfig {
  /** From address — must be a Resend-verified sender (e.g., "noreply@yourcompany.com") */
  fromAddress: string;

  /** From name — display name shown in the recipient's inbox (e.g., "Your Company") */
  fromName: string;

  /** Optional reply-to (defaults to fromAddress if omitted) */
  replyTo?: string;

  /** Public base URL where the magic link lands (e.g., "https://app.example.com").
   *  Used by requestEmailLogin to build the verification URL sent in the
   *  email body. No trailing slash. */
  appBaseUrl: string;
}

/**
 * Domain and infrastructure configuration
 */
export interface TenantDomain {
  /** Firebase project ID for this tenant (if separate projects) */
  firebaseProject?: string;

  /** Email domain for the organization (e.g., "yourcompany.com") */
  emailDomain?: string;

  /** Custom domain for the tenant (e.g., "app.yourcompany.com") */
  customDomain?: string;

  /** Subdomain prefix if using shared domain */
  subdomain?: string;
}

/**
 * Complete tenant configuration
 *
 * This is the main interface for tenant settings stored in Firestore
 *
 * @example
 * // Firestore document structure
 * tenants/techery-au {
 *   id: "techery-au",
 *   companyName: "The Techery",
 *   companyAcronym: "THEO",
 *   theme: { primaryColor: "#1a2238", ... },
 *   featureFlags: { enableFaultPipeline: true, ... },
 *   ...
 * }
 */
export interface TenantConfig {
  /** Unique tenant identifier (Firestore document ID) */
  id: string;

  /** Full company name (e.g., "The Techery") */
  companyName: string;

  /** Short company acronym/abbreviation (e.g., "THEO") */
  companyAcronym?: string;

  /** Company tagline or description (e.g., "Home of Efficient Operations") */
  companyTagline?: string;

  /** Full expanded tagline if acronym has meaning */
  companyTaglineExpanded?: string;

  /** System name for auto-generated messages (defaults to companyName) */
  systemName?: string;

  /**
   * User IDs of this tenant's owners/directors — the SOLE source of
   * director-only access (isDirector() in src/utils/access.ts, the Directors
   * nav pillar in OfficeLayout). When unset/empty, isDirector() returns false
   * for everyone — populate it per tenant via `npm run populate:owner-ids`.
   *
   * Added May 2026 per SaaS-readiness audit — replaced the hardcoded
   * DIRECTOR_NAMES name-list gate (fallback removed June 2026 once ownerIds
   * shipped). Destructive/admin-tool gates use the isAdmin custom claim
   * (isAdminSync()) rather than this field.
   */
  ownerIds?: string[];

  /** Color theme configuration */
  theme: TenantTheme;

  /** Visual branding assets */
  branding: TenantBranding;

  /** Business/company details */
  business: TenantBusiness;

  /** Feature toggle flags */
  featureFlags: TenantFeatureFlags;

  /** Domain and infrastructure settings */
  domain?: TenantDomain;

  /** Email-based passwordless login configuration (Phase 1.1)
   *  Optional — tenants without this fall back to PIN-only login. */
  emailLoginConfig?: TenantEmailLoginConfig;

  /**
   * Subscription tier — controls shell, route surface, multi-person features,
   * seat limits. Runtime defaults to 'pro' when missing (preserves existing
   * Techery behaviour without writing to the tenant doc).
   *
   * Lite: 1–3 person operations, no office/field split, mobile-first blended shell.
   * Pro: 4+ person operations, office/field split, OfficeLayout + TechLayout.
   *
   * Trade pack architecture (Phase 0).
   */
  tier?: 'lite' | 'pro';

  /**
   * Trade pack identifier — references trade_packs/{packId}. Tier-agnostic;
   * every pack must work cleanly on BOTH tiers. Runtime defaults to
   * 'telco-installer' when missing.
   *
   * The pack defines: service categories, intake/scope/completion field defs,
   * compliance flows, customer report templates, terminology overrides,
   * dashboard widgets, route gates.
   *
   * Trade pack architecture (Phase 0).
   */
  tradePackId?: string;

  /**
   * Hard seat cap for the tenant. 3 for lite, undefined (unlimited) for pro.
   * Enforced server-side on user creation (Phase 7 work). Phase 0 only stores
   * the value; no enforcement yet.
   */
  seatLimit?: number;

  /**
   * Feature packs — module entitlement (docs/FEATURE_PACKS_PLAN.md).
   * Pack IDs from `shared/src/domain/feature-packs.ts` ('standard', 'msp',
   * 'projects', 'provisioning'). Runtime rules live in resolveFeatures():
   * missing field → all features (preserves existing tenants without a doc
   * write); present → listed packs plus 'standard', which is always granted.
   * Written only by the platform console callables — never client-side.
   */
  featurePacks?: string[];

  /**
   * Lite-specific per-tenant overrides. Only consulted when tier === 'lite'.
   *
   * Trade pack architecture (Phase 0).
   */
  liteCustomizations?: {
    /** Widget keys to suppress on the Lite home dashboard. */
    hiddenWidgets?: string[];
    /** Tenant-specific terminology overrides on top of pack terminology. */
    customTerminology?: Record<string, string>;
  };

  /**
   * Tenant-wide proposal defaults — apply to every proposal unless a
   * template or per-proposal override sets its own value. Edited via
   * the Branding tab in `/admin/proposal-settings/branding`.
   *
   * `themeTokens` is a partial — only fields the office cared enough to
   * customise are stored; the renderer fills the rest from the template
   * or the global DEFAULT_PROPOSAL_THEME_TOKENS. `fromEmail` /
   * `fromName` are only consulted by the Cloud Function when the
   * tenant's verified sender domain matches the address.
   */
  proposalDefaults?: {
    themeTokens?: Partial<
      import('./proposal-premium.types').ProposalThemeTokens
    >;
    fromEmail?: string;
    fromName?: string;
  };

  /**
   * Per-surface magazine asset defaults (Phase C1, May 2026).
   *
   * Admin-configurable from the Branding tab at
   * `/admin/proposal-settings/branding` → "Hero photos". Each
   * surface gets one optional hero photo from the shared
   * `proposal_assets` library. Renderers fall through these layers:
   *
   *   1. Explicit per-document hero (e.g. `deal.heroPhotoOverride`,
   *      where a specific deal carries its own picked image)
   *   2. `tenant.magazineDefaults.heroAssets[surface]` — this tenant's
   *      picked default for the surface (Phase C1)
   *   3. Service-type matching from the asset library
   *      (`assets.heroForServiceType.{nbn|starlink|...}`)
   *   4. Tenant's `brandFallbackHeroUrl` (TenantInvoiceBrandConfig)
   *   5. Solid-colour banner (no hero) — the renderer always has a
   *      graceful no-hero path
   *
   * Storing per surface (rather than one universal hero) lets the
   * office pick a celebration photo for the post-job report that's
   * different from the "first contact" photo on a proposal email.
   *
   * Phase C2 wires these through every renderer; today the picker
   * UI persists the values + the invoice PDF hero resolver chain
   * already consults them. The other surfaces (proposal email,
   * payment page, receipt PDF, post-job report) read this in C2.
   */
  magazineDefaults?: {
    heroAssets?: Partial<
      Record<
        | 'invoice_email'
        | 'invoice_pdf'
        | 'payment_page'
        | 'receipt_pdf'
        | 'post_job_report'
        | 'proposal_email',
        {
          /** Public Storage URL of the picked asset. */
          url: string;
          /** Asset ID (proposal_assets/{tenantId}.assets[id]) so the
           *  picker can highlight the current selection. */
          assetId?: string;
          /** Caption / alt text. */
          alt?: string;
        }
      >
    >;
  };

  /**
   * Resend sender-domain state for proposal email.
   *
   * Written by `functions/src/email/resend-domains.ts` (createSenderDomain /
   * verifySenderDomain) with `.set({...}, { merge: true })`, read by
   * `ProposalSenderDomainPage`.
   *
   * ⚠️ This field existed on production tenant documents for months WITHOUT
   * being declared here — a Cloud Function `merge: true` write added it and
   * nothing in the type system or review surfaced it. It was found by the
   * 2026-07-27 security audit, not by any control. `TENANT_CONFIG_KEYS` +
   * `findUndeclaredTenantKeys()` below exist to stop that recurring; see
   * `src/types/__tests__/tenant-config-keys.test.ts`.
   *
   * `dnsRecords` are DKIM/SPF/DMARC values, which are public in DNS by
   * design — this is not credential material. Note the tenant document is
   * WORLD-READABLE (`allow read: if true`), so nothing secret may be added
   * here. See SECURITY_FIX_PLAN.md §2A.
   */
  proposalSenderDomain?: {
    domain?: string;
    fromLocalPart?: string;
    fromName?: string | null;
    replyTo?: string | null;
    replyToUpdatedAt?: string | null;
    resendDomainId?: string;
    verified?: boolean;
    status?: string;
    dnsRecords?: Array<{
      record?: string;
      name?: string;
      value?: string;
      type?: string;
      ttl?: string;
      status?: string;
      priority?: number;
    }>;
    createdAt?: string;
    lastVerificationAttempt?: string | null;
  };

  /** Whether this tenant is active */
  isActive: boolean;

  /** Tenant creation timestamp (ISO string) */
  createdAt: string;

  /** Last update timestamp (ISO string) */
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Tenant document key-set guard
// ---------------------------------------------------------------------------

/**
 * Every key declared on `TenantConfig`, as a runtime value.
 *
 * WHY THIS EXISTS
 * ---------------
 * `tenants/{tenantId}` is world-readable (`allow read: if true` — the
 * pre-auth landing page needs branding). Any Cloud Function doing a
 * `.set(..., { merge: true })` can therefore add a field to a publicly
 * readable document, and TypeScript will never notice, because the write
 * happens in `functions/` against an untyped `Record<string, unknown>`.
 * That is exactly how `proposalSenderDomain` appeared above.
 *
 * HOW IT STAYS HONEST
 * -------------------
 * The `_exhaustive` map below is typed `Record<keyof TenantConfig, true>`.
 * Add a field to `TenantConfig` without adding it here and the build fails
 * with "Property 'x' is missing". Remove a field from the interface but
 * leave it here and the build fails with "Object literal may only specify
 * known properties". The array cannot silently drift from the type.
 *
 * Pair it with `findUndeclaredTenantKeys()` to check a real document.
 */
const _exhaustive: Record<keyof TenantConfig, true> = {
  id: true,
  companyName: true,
  companyAcronym: true,
  companyTagline: true,
  companyTaglineExpanded: true,
  systemName: true,
  ownerIds: true,
  theme: true,
  branding: true,
  business: true,
  featureFlags: true,
  domain: true,
  emailLoginConfig: true,
  tier: true,
  tradePackId: true,
  seatLimit: true,
  featurePacks: true,
  liteCustomizations: true,
  proposalDefaults: true,
  magazineDefaults: true,
  proposalSenderDomain: true,
  isActive: true,
  createdAt: true,
  updatedAt: true,
};

/** Frozen list of every key `TenantConfig` declares. */
export const TENANT_CONFIG_KEYS: readonly (keyof TenantConfig)[] = Object.freeze(
  Object.keys(_exhaustive) as (keyof TenantConfig)[],
);

/**
 * Returns the keys present on a tenant document that `TenantConfig` does not
 * declare. Empty array means the document matches the type.
 *
 * Intended uses:
 *   - unit test against a captured production shape
 *     (`src/types/__tests__/tenant-config-keys.test.ts`)
 *   - a dev-mode warning in `TenantContext` when a live document drifts
 *   - an admin audit script
 *
 * Deliberately tolerant of a null/non-object input (returns `[]`) so a
 * caller can pass a possibly-missing document without guarding first.
 */
export function findUndeclaredTenantKeys(doc: unknown): string[] {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return [];
  const declared = new Set<string>(TENANT_CONFIG_KEYS as readonly string[]);
  return Object.keys(doc as Record<string, unknown>)
    .filter((k) => !declared.has(k))
    .sort();
}

/**
 * User extension for multi-tenancy
 *
 * Add this field to the existing User interface in schema.ts
 */
export interface TenantUserFields {
  /** Reference to the tenant this user belongs to */
  tenantId: string;
}

/**
 * Context value provided by TenantProvider
 */
export interface TenantContextValue {
  /** Current tenant configuration (null if not loaded) */
  tenant: TenantConfig | null;

  /** Whether tenant config is currently loading */
  isLoading: boolean;

  /** Error message if tenant loading failed */
  error: string | null;

  /** Function to reload tenant config */
  refreshTenant: () => Promise<void>;
}

/**
 * Sentinel tenant ID used by TenantContext's neutral FALLBACK_TENANT_CONFIG.
 *
 * Any Firestore write that ends up tagged with this value indicates a code
 * path running while no real tenant config was loaded — a bug. Consumers can
 * `if (tenant.id === UNCONFIGURED_TENANT_ID)` to detect the error state.
 *
 * Lives here (not in TenantContext) so leaf utilities like
 * `src/utils/access.ts` can check it without importing the context module
 * (which itself imports from access.ts — a circular dependency).
 */
export const UNCONFIGURED_TENANT_ID = '__unconfigured__';

/**
 * Default feature flags (all disabled)
 *
 * Use this when creating new tenants or as fallback
 */
export const DEFAULT_FEATURE_FLAGS: TenantFeatureFlags = {
  enableFaultPipeline: false,
  enableProjectManagement: false,
  enableProvisioning: false,
  enablePlayground: false,
  enableTechConsults: false,
  enableStarlinkSupport: false,
  enableQuoteGeneration: false,
  enableAIGreetings: false,
  enableWeeklyQuiz: false,
  enableEmployeeVoting: false,
  enableMessaging: false,
  enableCustomerPortal: false,
  enableInventory: false,
  enableTimesheets: false,
  enableMaps: false,
  enableUnifiDesignImport: false,
  enableDealWorkspace: false,
  // Projects v2 dev workspace — opt-in per tenant. Techery sets true.
  enableProjectsV2: false,
  // Projects v2 production cutover — OFF until the live-jobs flip (Phase 10).
  enableProjectsV2Cutover: false,
  // Phase 4 — defaults during the BP→native parallel-run window:
  useNativeProposals: false,
  enableLegacyBetterProposals: true,
  // P2.0.8 — off by default, opt in per tenant after E2E testing:
  enableDepositInvoices: false,
  // Proposal Builder V2 — off until flipped per tenant (admins see it anyway):
  proposalBuilderV2: false,
};

/**
 * Default theme colors (Techery brand as baseline)
 *
 * New tenants can use this as a starting point
 */
export const DEFAULT_THEME: TenantTheme = {
  primaryColor: '#1a2238',
  secondaryColor: '#c5a065',
  accentColor: '#e2e8f0',
  darkColor: '#0f1521',
  successColor: '#10b981',
  dangerColor: '#ef4444',
  warningColor: '#f59e0b',
};

/**
 * Type guard to check if an object is a valid TenantConfig
 */
export function isTenantConfig(obj: unknown): obj is TenantConfig {
  if (typeof obj !== 'object' || obj === null) return false;

  const tenant = obj as Partial<TenantConfig>;

  return (
    typeof tenant.id === 'string' &&
    typeof tenant.companyName === 'string' &&
    typeof tenant.theme === 'object' &&
    typeof tenant.featureFlags === 'object' &&
    typeof tenant.isActive === 'boolean'
  );
}
